<div
  class="relative w-full max-w-[468px] mx-auto min-h-[240px] sm:min-h-[255px] aspect-[1.42/1] sm:aspect-[1.586/1] cursor-pointer group outline-none focus-visible:ring-2 focus-visible:ring-primary-9 rounded-[1.25rem] select-none"
  [style.perspective]="'1000px'"
  (click)="toggleFlip()"
  (keydown.enter)="toggleFlip()"
  (keydown.space)="toggleFlip(); $event.preventDefault()"
  tabindex="0"
  role="button"
  aria-label="Cliquer pour retourner la carte professionnelle"
>
  <!-- Card Container -->
  <div
    class="w-full h-full relative transition-transform duration-700 shadow-xl group-hover:shadow-2xl rounded-[1.25rem] border border-neutral-3 dark:border-neutral-4"
    [class.grayscale-[30%]]="isCardExpired()"
    [class.opacity-90]="isCardExpired()"
    [class.border-red-6]="isCardExpired()"
    [style.transformStyle]="'preserve-3d'"
    [style.transform]="isFlipped() ? 'rotateY(180deg)' : 'rotateY(0deg)'"
  >
    <!-- FRONT FACE -->
    <div
      class="absolute inset-0 w-full h-full rounded-[1.25rem] bg-white dark:bg-neutral-2 overflow-hidden"
      [style.backfaceVisibility]="'hidden'"
    >
      <!-- Watermark filigrane discret et translucide (Front uniquement) -->
      @if (isCardExpired()) {
        <div
          class="absolute inset-0 z-20 pointer-events-none flex items-center justify-center overflow-hidden"
        >
          <div
            class="transform -rotate-20 border-2 border-dashed border-red-9/40 text-red-11/70 bg-red-1/40 dark:bg-red-2/30 dark:text-red-11 px-5 sm:px-7 py-1.5 rounded-xl select-none flex items-center gap-2 tracking-[0.25em] font-extrabold text-xs sm:text-sm uppercase opacity-60 backdrop-blur-[1px]"
          >
            <mat-icon class="!w-4 !h-4 !text-base text-red-9/60">block</mat-icon>
            <span>Carte expirée</span>
          </div>
        </div>
      }

      <!-- Decorative Background (Guilloché inspiration) -->
      <div
        class="absolute inset-0 bg-gradient-to-br from-primary-2 to-primary-1 dark:from-primary-a3 dark:to-primary-a2 opacity-80 pointer-events-none"
      ></div>
      <!-- Subtle concentric circles -->
      <div
        class="absolute -left-12 -top-12 w-64 h-64 border-[30px] border-primary-3 dark:border-primary-4 opacity-20 dark:opacity-10 rounded-full mix-blend-multiply dark:mix-blend-screen pointer-events-none"
      ></div>
      <div
        class="absolute -left-8 -top-8 w-56 h-56 border-[15px] border-primary-4 dark:border-primary-5 opacity-20 dark:opacity-10 rounded-full mix-blend-multiply dark:mix-blend-screen pointer-events-none"
      ></div>

      <!-- Content -->
      <div class="relative z-10 w-full h-full p-3.5 sm:p-4.5 flex flex-col justify-between">
        <!-- Header -->
        <div class="flex justify-between items-start gap-2">
          <div class="flex items-center gap-2">
            <div
              class="bg-primary-9 text-white text-[8.5px] sm:text-[9px] font-bold px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md w-max uppercase leading-tight shadow-sm tracking-wider"
            >
              Chambre de Commerce<br />et d'Industrie
            </div>
          </div>

          <div class="text-right">
            <h2
              class="text-primary-11 text-[14px] sm:text-[16px] font-bold uppercase tracking-tight leading-none"
            >
              Carte<br />Professionnelle
            </h2>
          </div>
        </div>

        <div class="mt-1.5 sm:mt-3 flex justify-between gap-2 items-start">
          <div
            class="text-[8.5px] sm:text-[10px] font-bold text-primary-11 uppercase leading-tight flex-1 min-w-0"
          >
            CCI <span class="font-normal truncate block">{{ card()?.cci?.name || 'Non renseignée' }}</span>
            <div class="mt-1">
              <p
                class="text-[8px] sm:text-[10px] font-bold text-neutral-8 dark:text-neutral-9 uppercase tracking-wider"
              >
                Représentant Légal
              </p>
              <p
                class="text-[8.5px] sm:text-[10px] font-bold text-neutral-12 truncate"
                [title]="(card()?.leader?.lastName || '') + ' ' + (card()?.leader?.firstName || '')"
              >
                {{ card()?.leader ? card()?.leader?.lastName + ' • ' + card()?.leader?.firstName : '-' }}
              </p>
            </div>
          </div>

          <div class="text-right flex-1 min-w-0">
            <p
              class="text-[8.5px] sm:text-[10px] font-bold text-primary-11 uppercase tracking-widest mb-0.5 truncate"
            >
              N° {{ card()?.number || 'NON RENSEIGNÉ' }}
            </p>
            <p
              class="text-[8.5px] sm:text-[10px] text-neutral-8 dark:text-neutral-9 font-medium whitespace-nowrap"
            >
              Valable jusqu'au:
              <span class="text-neutral-12 font-bold">{{
                card()?.expiresAt ? ($safeNavigationMigration(card()?.expiresAt) | date: 'dd/MM/yyyy') : '-'
              }}</span>
            </p>
            @if (card()?.cci?.president) {
              <div class="mt-1">
                <p
                  class="text-[8px] sm:text-[10px] font-bold text-neutral-8 dark:text-neutral-9 uppercase tracking-wider"
                >
                  Président de la CCI
                </p>
                <p
                  class="text-[8.5px] sm:text-[10px] font-bold text-neutral-12 truncate"
                  [title]="
                    (card()?.cci?.president?.firstName || '') + ' ' + (card()?.cci?.president?.lastName || '')
                  "
                >
                  {{
                    card()?.cci?.president
                      ? card()?.cci?.president?.firstName + ' ' + card()?.cci?.president?.lastName
                      : '-'
                  }}
                </p>
              </div>
            }
          </div>
        </div>

        <!-- Footer -->
        <div
          class="mt-auto pt-1.5 sm:pt-2 flex justify-between items-end border-t border-primary-4 dark:border-primary-5/40 relative gap-2"
        >
          <div class="text-[8.5px] sm:text-[10px] leading-tight flex-1 min-w-0">
            <span
              class="font-bold text-neutral-8 dark:text-neutral-9 uppercase text-[8px] sm:text-[10px] tracking-wider"
              >Titulaire de la carte :</span
            ><br />
            <span
              class="font-bold text-neutral-12 text-[10.5px] sm:text-xs truncate block"
              [title]="card()?.companyName || ''"
              >{{ card()?.companyName || '-' }}</span
            >
            @if (card()?.address; as address) {
              <span
                class="text-neutral-8 dark:text-neutral-9 text-[8.5px] sm:text-[10px] leading-tight block truncate"
              >
                {{ address.streetNumber }} {{ address.streetType }} {{ address.streetName }},
                {{ address.zipCode }} {{ address.city }}
              </span>
            }
            <span
              class="font-bold text-neutral-8 dark:text-neutral-9 uppercase text-[9.5px] sm:text-[12px] tracking-wider font-mono block mt-0.5"
              >SIREN: {{ card()?.siren || '-' }}</span
            >
          </div>

          <div
            class="text-[8.5px] sm:text-[9px] text-right text-primary-11 italic flex items-center gap-1 font-medium bg-white/60 dark:bg-neutral-3/70 px-2 sm:px-2 py-1 sm:py-1 rounded-full backdrop-blur-sm shrink-0 border border-primary-4/50 dark:border-primary-5/40 shadow-xs"
          >
            <mat-icon class="!w-3 !h-3 text-[11px] sm:text-[12px] leading-3">flip</mat-icon>
            Voir verso
          </div>
        </div>
      </div>
    </div>

    <!-- BACK FACE -->
    <div
      class="absolute inset-0 w-full h-full rounded-[1.25rem] bg-white dark:bg-neutral-2 overflow-hidden"
      [style.backfaceVisibility]="'hidden'"
      [style.transform]="'rotateY(180deg)'"
    >
      <!-- Decorative Background (Guilloché inspiration) -->
      <div
        class="absolute inset-0 bg-gradient-to-br from-primary-2 to-primary-1 dark:from-primary-a3 dark:to-primary-a2 opacity-80 pointer-events-none"
      ></div>
      <!-- Subtle concentric circles -->
      <div
        class="absolute -right-12 -bottom-12 w-64 h-64 border-[30px] border-primary-3 dark:border-primary-4 opacity-20 dark:opacity-10 rounded-full mix-blend-multiply dark:mix-blend-screen pointer-events-none"
      ></div>
      <div
        class="absolute -right-8 -bottom-8 w-56 h-56 border-[15px] border-primary-4 dark:border-primary-5 opacity-20 dark:opacity-10 rounded-full mix-blend-multiply dark:mix-blend-screen pointer-events-none"
      ></div>

      <!-- Content -->
      <div class="relative z-10 w-full h-full p-3.5 sm:p-4.5 flex flex-col justify-between">
        <div class="space-y-2 sm:space-y-3">
          <div class="border-b border-primary-4 dark:border-primary-5/40 pb-1 sm:pb-1.5">
            <p
              class="text-[8px] sm:text-[9.5px] font-bold text-neutral-8 dark:text-neutral-9 uppercase tracking-wider"
            >
              Permettant l'exercice de l'activité de :
            </p>
            <p class="text-[10px] sm:text-[11.5px] font-bold text-neutral-12 leading-tight mt-0.5">
              @if (card()?.type === 'titulaire' || card()?.mentions) {
                Transactions sur immeubles et fonds de commerce
              } @else {
                Attestation de collaborateur (Mandataire)
              }
            </p>
          </div>

          @if (card()?.mentions) {
            <div>
              <p
                class="text-[8px] sm:text-[9.5px] font-bold text-neutral-8 dark:text-neutral-9 uppercase tracking-wider mb-0.5 sm:mb-1"
              >
                Mentions (Activités)
              </p>
              <div class="flex flex-wrap gap-1 sm:gap-1.5">
                @if (card()?.mentions?.transaction) {
                  <span
                    class="text-[8.5px] sm:text-[10px] bg-primary-2 dark:bg-primary-3/30 border border-primary-4 dark:border-primary-5/40 text-primary-11 px-2 py-0.5 rounded-full font-bold"
                    >Transaction</span
                  >
                }
                @if (card()?.mentions?.management) {
                  <span
                    class="text-[8.5px] sm:text-[10px] bg-primary-2 dark:bg-primary-3/30 border border-primary-4 dark:border-primary-5/40 text-primary-11 px-2 py-0.5 rounded-full font-bold"
                    >Gestion</span
                  >
                }
                @if (card()?.mentions?.syndic) {
                  <span
                    class="text-[8.5px] sm:text-[10px] bg-primary-2 dark:bg-primary-3/30 border border-primary-4 dark:border-primary-5/40 text-primary-11 px-2 py-0.5 rounded-full font-bold"
                    >Syndic</span
                  >
                }
              </div>
            </div>
          }

          @if (card()?.insurer; as insurer) {
            <div>
              <p
                class="text-[8px] sm:text-[9.5px] font-bold text-neutral-8 dark:text-neutral-9 uppercase tracking-wider"
              >
                Assurance RCP
              </p>
              <p class="text-[10px] sm:text-[12px] font-bold text-neutral-12 leading-tight">
                {{ insurer.name }}
              </p>
              @if (insurer.address; as address) {
                <p
                  class="text-[8.5px] sm:text-[10px] text-neutral-8 dark:text-neutral-9 leading-tight truncate"
                >
                  {{ address.streetNumber }} {{ address.streetType }} {{ address.streetName }},
                  {{ address.zipCode }} {{ address.city }}
                </p>
              }
            </div>
          }
        </div>

        <div
          class="mt-auto shrink-0 pt-1.5 border-t border-primary-4 dark:border-primary-5/40 flex items-center justify-between gap-2"
        >
          <p
            class="text-[7.5px] sm:text-[8.5px] text-justify text-neutral-8 dark:text-neutral-9 leading-tight flex-1"
          >
            La présente carte est délivrée conformément aux dispositions de la loi n° 70-9 du 2 janvier 1970
            modifiée. Elle doit être restituée à la CCI en cas de cessation d'activité.
          </p>

          <div
            class="text-[8.5px] sm:text-[9px] text-right text-primary-11 italic flex items-center gap-1 font-medium bg-white/60 dark:bg-neutral-3/70 px-2 sm:px-2 py-1 sm:py-1 rounded-full backdrop-blur-sm shrink-0 border border-primary-4/50 dark:border-primary-5/40 shadow-xs"
          >
            <mat-icon class="!w-3 !h-3 text-[11px] sm:text-[12px] leading-3">flip</mat-icon>
            Voir recto
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
